/**
 * Active Position Hooks
 * TanStack Query hooks for active positions and snapshots
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { activePositionRepo } from '@/lib/repositories'
import { requireSupabaseClient } from '@/lib/supabase'
import type {
  CreatePositionData,
  UpdatePositionData,
  ActivePosition,
} from '@/lib/repositories'
import { analytics } from '@/lib/services/analytics'
import { AnalyticsEvents } from '@/lib/services/analytics-events'

export const ACTIVE_POSITIONS_KEY = ['active-positions'] as const
export const POSITION_SNAPSHOTS_KEY = ['position-snapshots'] as const

export function useActivePositions() {
  return useQuery({
    queryKey: ACTIVE_POSITIONS_KEY,
    queryFn: () => activePositionRepo.getPositions(),
    staleTime: 30_000,
  })
}

export function usePositionSnapshots(days: number = 30) {
  return useQuery({
    queryKey: [...POSITION_SNAPSHOTS_KEY, days],
    queryFn: () => activePositionRepo.getSnapshots(days),
    staleTime: 5 * 60_000,
  })
}

async function backfillPosition(params: {
  positionId: string
  ticker: string
  buyInPrice: number
  fromDate: string
  clearExisting?: boolean
}): Promise<{ success: boolean; count: number }> {
  const supabase = requireSupabaseClient()

  const { data, error } = await supabase.functions.invoke(
    'backfill-position',
    { body: params }
  )

  if (error) {
    console.error('Backfill failed:', error)
    return { success: false, count: 0 }
  }

  return data as { success: boolean; count: number }
}

export function useCreatePosition() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePositionData) =>
      activePositionRepo.createPosition(data),
    onSuccess: async (created: ActivePosition, variables: CreatePositionData) => {
      queryClient.invalidateQueries({ queryKey: ACTIVE_POSITIONS_KEY })
      analytics.capture(AnalyticsEvents.POSITION_CREATED, { ticker: created.ticker })

      // Backfill historical snapshots, then refresh chart
      const fromDate =
        variables.openedAt || created.createdAt.slice(0, 10)

      await backfillPosition({
        positionId: created.id,
        ticker: created.ticker,
        buyInPrice: created.buyInPrice,
        fromDate,
      })

      queryClient.invalidateQueries({ queryKey: POSITION_SNAPSHOTS_KEY })
    },
  })
}

export function useUpdatePosition() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: UpdatePositionData
      previousTicker?: string
      previousOpenedAt?: string
    }) => activePositionRepo.updatePosition(id, data),
    onSuccess: async (
      updated: ActivePosition,
      variables: {
        id: string
        data: UpdatePositionData
        previousTicker?: string
        previousOpenedAt?: string
      }
    ) => {
      queryClient.invalidateQueries({ queryKey: ACTIVE_POSITIONS_KEY })
      analytics.capture(AnalyticsEvents.POSITION_UPDATED, { ticker: updated.ticker })

      const tickerChanged =
        variables.previousTicker !== undefined &&
        variables.previousTicker !== updated.ticker
      const openedAtChanged =
        variables.previousOpenedAt !== undefined &&
        variables.previousOpenedAt !== (updated.openedAt || '')

      if (tickerChanged || openedAtChanged) {
        const fromDate =
          updated.openedAt || updated.createdAt.slice(0, 10)

        await backfillPosition({
          positionId: updated.id,
          ticker: updated.ticker,
          buyInPrice: updated.buyInPrice,
          fromDate,
          clearExisting: tickerChanged,
        })

        queryClient.invalidateQueries({ queryKey: POSITION_SNAPSHOTS_KEY })
      }
    },
  })
}

export function useDeletePosition() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => activePositionRepo.deletePosition(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACTIVE_POSITIONS_KEY })
      queryClient.invalidateQueries({ queryKey: POSITION_SNAPSHOTS_KEY })
      analytics.capture(AnalyticsEvents.POSITION_DELETED)
    },
  })
}
